/**
 * Storage utilities with C2 prefix (Corgee v2)
 * Provides consistent access to localStorage and sessionStorage
 */

import { STORAGE_KEYS } from './constants';
import { clearViewCacheForLogout } from './viewCache';

// ==================== TypeScript Types ====================

/**
 * Auth data stored in localStorage
 */
export interface AuthStorageData {
  access_token: string;
  refresh_token: string;
  username: string;
  givenname: string;
  personid: string;
  layerid: string;
  imageprofile: string;
  groupId: string;
  groupName: string;
  defaultGroupId: string;
  defaultGroupName: string;
  nGroups: string;
}

/**
 * Session data stored in sessionStorage (permissions, etc.)
 */
export interface SessionStorageData {
  isAdmin: boolean;
  isBeta: boolean;
  isBetaPermission: boolean;
  isAdminProject: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canTemporary: boolean;
  ExternalLogin: boolean;
  External: boolean;
  SupportPermissions: boolean;
  EmailAddress: string;
  PTOApprover: boolean;
  HRApprover: boolean;
}

/**
 * Dashboard state stored in localStorage
 */
export interface DashboardState {
  projectId: number | null;
  configValueId: number; // Distinguishes Dashboards / Bookmarks / CardTypes / Projects
  index: number;
  dashboardId: number;
  DashboardViewId?: number;
  GridSize?: number;
  lastModified: number; // Unix ms – used for LRU eviction
}

/** Legacy key (without dashboardId) for backwards-compat lookup */
function _legacyKey(projectId: number | null, configValueId: number): string
{
  return `${projectId || 0}_${configValueId}`;
}

// ==================== Auth Storage ====================

/**
 * Auth storage utility - manages authentication data
 */
export const authStorage = {
  /**
   * Get the access token
   */
  getAccessToken(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * Set the access token
   */
  setAccessToken(token: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  },

  /**
   * Get the refresh token
   */
  getRefreshToken(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  /**
   * Set the refresh token
   */
  setRefreshToken(token: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  },

  /**
   * Get username
   */
  getUsername(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.USERNAME);
  },

  /**
   * Set username
   */
  setUsername(username: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.USERNAME, username);
  },

  /**
   * Get given name
   */
  getGivenName(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.GIVENNAME);
  },

  /**
   * Set given name
   */
  setGivenName(name: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.GIVENNAME, name);
  },

  /**
   * Get person ID
   */
  getPersonId(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.PERSONID);
  },

  /**
   * Set person ID
   */
  setPersonId(id: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.PERSONID, id);
  },

  /**
   * Get layer ID
   */
  getLayerId(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.LAYERID);
  },

  /**
   * Set layer ID
   */
  setLayerId(id: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.LAYERID, id);
  },

  /**
   * Get image profile URL
   */
  getImageProfile(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.IMAGEPROFILE);
  },

  /**
   * Set image profile URL
   */
  setImageProfile(url: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.IMAGEPROFILE, url);
  },

  /**
   * Get group ID
   */
  getGroupId(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.GROUP_ID);
  },

  /**
   * Set group ID
   */
  setGroupId(id: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.GROUP_ID, id);
  },

  /**
   * Get default group ID
   */
  getDefaultGroupId(): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.DEFAULT_GROUP_ID);
  },

  /**
   * Set default group ID
   */
  setDefaultGroupId(id: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.DEFAULT_GROUP_ID, id);
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean
  {
    return !!this.getAccessToken();
  },

  /**
   * Clear all auth data
   */
  clearAuth(): void
  {
    if (typeof window === 'undefined') return;
    // Purge view cache and block writes from in-flight requests
    clearViewCacheForLogout();
    // Limpiar todas las llaves C2_ de localStorage
    Object.keys(localStorage).forEach(key =>
    {
      if (key.startsWith('C2_'))
      {
        localStorage.removeItem(key);
      }
    });
    // Also clear specific session keys
    this.clearSessionData();
  },

  /**
   * Clear session data (permissions, etc.)
   */
  clearSessionData(): void
  {
    if (typeof window === 'undefined') return;
    // Limpiar todas las llaves C2_ de sessionStorage
    Object.keys(window.sessionStorage).forEach(key =>
    {
      if (key.startsWith('C2_'))
      {
        window.sessionStorage.removeItem(key);
      }
    });
  },

  /**
   * Get tenant data from localStorage
   */
  getTenantData(): any | null
  {
    if (typeof window === 'undefined') return null;
    try
    {
      const data = localStorage.getItem(STORAGE_KEYS.TENANT_DATA);
      return data ? JSON.parse(data) : null;
    }
    catch
    {
      return null;
    }
  },

  /**
   * Set tenant data in localStorage
   */
  setTenantData(data: any): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.TENANT_DATA, JSON.stringify(data));
  },

  /**
   * Remove tenant data from localStorage
   */
  removeTenantData(): void
  {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(STORAGE_KEYS.TENANT_DATA);
  },
};

// ==================== Dashboard Storage ====================

/**
 * Dashboard storage utility - manages dashboard-related data
 */
export const dashboardStorage = {
  /**
   * Get current dashboard state
   */
  getCurrentDashboard(): DashboardState[]
  {
    if (typeof window === 'undefined') return [];
    try
    {
      const data = localStorage.getItem(STORAGE_KEYS.CURRENT_DASHBOARD);
      if (!data) return [];

      const parsed = JSON.parse(data);
      // Ensure we always return an array (handle corrupted data)
      if (!Array.isArray(parsed))
      {
        console.warn('⚠️ Dashboard storage corrupted, resetting to empty array');
        this.setCurrentDashboard([]);
        return [];
      }
      return parsed;
    }
    catch (error)
    {
      console.error('❌ Error parsing dashboard storage:', error);
      // Clear corrupted data
      localStorage.removeItem(STORAGE_KEYS.CURRENT_DASHBOARD);
      return [];
    }
  },

  /**
   * Set current dashboard state
   */
  setCurrentDashboard(state: DashboardState[]): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.CURRENT_DASHBOARD, JSON.stringify(state));
  },

  /**
   * Build a unique string key from projectId + configValueId + dashboardId
   */
  _key(projectId: number | null, configValueId: number, dashboardId?: number): string
  {
    return `${projectId || 0}_${configValueId}_${dashboardId || 0}`;
  },

  /**
   * Update (or insert) an entry, stamping lastModified and enforcing the 30-item LRU cap.
   * Evicts the entry with the oldest lastModified when the list exceeds 30.
   */
  updateDashboard(entry: DashboardState): void
  {
    const list = this.getCurrentDashboard();
    const key = this._key(entry.projectId, entry.configValueId, entry.dashboardId);
    const existingIndex = list.findIndex(
      item => this._key(item.projectId, item.configValueId ?? 1, item.dashboardId) === key,
    );

    const updated: DashboardState = {
      ...entry,
      DashboardViewId: entry.DashboardViewId || 1,
      lastModified: Date.now(),
    };

    if (existingIndex !== -1)
    {
      list[existingIndex] = { ...list[existingIndex], ...updated };
    }
    else
    {
      list.push(updated);
      // Enforce max 30: evict the entry that was least recently modified
      if (list.length > 30)
      {
        list.sort((a, b) => (a.lastModified ?? 0) - (b.lastModified ?? 0));
        list.shift(); // remove the oldest entry (index 0 after sort)
      }
    }
    this.setCurrentDashboard(list);
  },

  /**
   * Find saved state by context (projectId + configValueId + dashboardId).
   * Falls back to legacy 2-part key for entries saved before dashboardId was added.
   */
  findByContext(
    projectId: number | null,
    configValueId: number,
    dashboardId?: number,
  ): DashboardState | null
  {
    const list = this.getCurrentDashboard();

    // Safety check: ensure list is an array
    if (!Array.isArray(list))
    {
      console.error('❌ getCurrentDashboard did not return an array:', list);
      return null;
    }

    // Try exact match with dashboardId first
    if (dashboardId)
    {
      const key = this._key(projectId, configValueId, dashboardId);
      const exact = list.find(
        item => item && this._key(item.projectId, item.configValueId ?? 1, item.dashboardId) === key,
      );
      if (exact) return exact;
    }
    // Fallback: legacy match without dashboardId (migration path)
    const legacyKey = _legacyKey(projectId, configValueId);
    return (
      list.find(
        item => item && _legacyKey(item.projectId, item.configValueId ?? 1) === legacyKey,
      ) ?? null
    );
  },

  /**
   * @deprecated Use findByContext instead.
   */
  findByProjectId(projectId: number | null): DashboardState | null
  {
    return this.findByContext(projectId, 1);
  },

  /**
   * Get dashboard index for a project (legacy helper)
   */
  getDashboardIndex(projectId: number | null): number
  {
    const item = this.findByContext(projectId, 1);
    return item?.index ?? 0;
  },

  /**
   * Get hidden layers
   */
  getHiddenLayers(): number[]
  {
    if (typeof window === 'undefined') return [];
    try
    {
      const data = localStorage.getItem(STORAGE_KEYS.HIDDEN_LAYERS);
      return data ? JSON.parse(data) : [];
    }
    catch
    {
      return [];
    }
  },

  /**
   * Set hidden layers
   */
  setHiddenLayers(layers: number[]): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.HIDDEN_LAYERS, JSON.stringify(layers));
  },

  /**
   * Toggle layer visibility
   */
  toggleLayer(layerId: number): number[]
  {
    const hiddenLayers = this.getHiddenLayers();
    const newHiddenLayers = hiddenLayers.includes(layerId)
      ? hiddenLayers.filter(id => id !== layerId)
      : [...hiddenLayers, layerId];
    this.setHiddenLayers(newHiddenLayers);
    return newHiddenLayers;
  },

  /**
   * Get dashboard config
   */
  getDashboardConfig(): Record<string, any> | null
  {
    if (typeof window === 'undefined') return null;
    try
    {
      const data = localStorage.getItem(STORAGE_KEYS.DASHBOARD_CONFIG);
      return data ? JSON.parse(data) : null;
    }
    catch
    {
      return null;
    }
  },

  /**
   * Set dashboard config
   */
  setDashboardConfig(config: Record<string, any>): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.DASHBOARD_CONFIG, JSON.stringify(config));
  },
};

// ==================== Session Storage ====================

/**
 * Session storage utility - manages session data (permissions, etc.)
 */
export const sessionStorageUtil = {
  /**
   * Get person data from session storage
   */
  getPersonData(): SessionStorageData | null
  {
    if (typeof window === 'undefined') return null;
    try
    {
      const data = window.sessionStorage.getItem('C2_data');
      return data ? JSON.parse(data) : null;
    }
    catch
    {
      return null;
    }
  },

  /**
   * Set person data in session storage
   */
  setPersonData(data: SessionStorageData): void
  {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem('C2_data', JSON.stringify(data));
  },

  /**
   * Get all layers from session storage
   */
  getAllLayers(): any[]
  {
    if (typeof window === 'undefined') return [];
    try
    {
      const data = window.sessionStorage.getItem('C2_AllLayers');
      return data ? JSON.parse(data) : [];
    }
    catch
    {
      return [];
    }
  },

  /**
   * Set all layers in session storage
   */
  setAllLayers(layers: any[]): void
  {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem('C2_AllLayers', JSON.stringify(layers));
  },

  /**
   * Get layers to hide from session storage
   */
  getLayersToHide(): any[] | false
  {
    if (typeof window === 'undefined') return false;
    try
    {
      const data = window.sessionStorage.getItem('C2_LayersToHide');
      return data ? JSON.parse(data) : false;
    }
    catch
    {
      return false;
    }
  },

  /**
   * Set layers to hide in session storage
   */
  setLayersToHide(layers: any[]): void
  {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem('C2_LayersToHide', JSON.stringify(layers));
  },
};

// ==================== Generic Storage ====================

/**
 * Generic storage utility - provides safe localStorage/sessionStorage access
 */
export const storage = {
  /**
   * Get item from localStorage
   */
  getItem(key: string): string | null
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(key);
  },

  /**
   * Set item in localStorage
   */
  setItem(key: string, value: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, value);
  },

  /**
   * Remove item from localStorage
   */
  removeItem(key: string): void
  {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
  },

  /**
   * Get parsed JSON from localStorage
   */
  getJSON<T>(key: string): T | null
  {
    if (typeof window === 'undefined') return null;
    try
    {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    }
    catch
    {
      return null;
    }
  },

  /**
   * Set JSON in localStorage
   */
  setJSON(key: string, value: unknown): void
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, JSON.stringify(value));
  },

  /**
   * Get item from sessionStorage
   */
  getSessionItem(key: string): string | null
  {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(key);
  },

  /**
   * Set item in sessionStorage
   */
  setSessionItem(key: string, value: string): void
  {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(key, value);
  },

  /**
   * Remove item from sessionStorage
   */
  removeSessionItem(key: string): void
  {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(key);
  },

  /**
   * Get parsed JSON from sessionStorage
   */
  getSessionJSON<T>(key: string): T | null
  {
    if (typeof window === 'undefined') return null;
    try
    {
      const data = window.sessionStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    }
    catch
    {
      return null;
    }
  },

  /**
   * Set JSON in sessionStorage
   */
  setSessionJSON(key: string, value: unknown): void
  {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(key, JSON.stringify(value));
  },
};

// Export all utilities
export default {
  auth: authStorage,
  dashboard: dashboardStorage,
  session: sessionStorageUtil,
  storage,
};
